import React, { useState, memo, useCallback } from 'react';
import { countBy, sortBy, isEmpty, groupBy, findIndex } from 'lodash';
import moment from 'moment';
import IconButton from '@material-ui/core/IconButton';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import RemoveOutlinedIcon from '@mui/icons-material/RemoveOutlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';

import Select from 'components/ui/Select';
import AddAppointmentModal from 'components/pages/Clinics/Clinic/Calendar/AddAppointmentModal';
import { useSnackBar } from 'context/SnackBarContext';
import { showClinic, toggleBlockSlots } from 'api/clinic';
import {
  Component,
  DateBar,
  Navigation,
  Date,
  ActionBar,
  ActionButton,
  BlockOffMenu,
  Slots,
  SlotButton,
  BlockMenuHeading,
  BlockActions,
  BlockCount,
  Slot,
  IconButtonWrapper,
} from './styles';

const ClinicToolbar = ({
  startDate,
  endDate,
  multiDays,
  toolbar,
  clinic,
  clinicStatus,
  selectedDate,
  setSelectedDate,
  fetchAppointments,
  concurrentSlots,
}) => {
  const { date } = toolbar || {};

  const [slotsToBlock, setSlotsToBlock] = useState([]);
  const [slotsToUnblock, setSlotsToUnblock] = useState([]);

  if (isEmpty(clinic)) {
    return null;
  }

  const [slots, setSlots] = useState([]);
  const [blockOffOpen, setBlockOffOpen] = useState(false);
  const [appointmentModalOpen, setAppointmentModalOpen] = useState(false);
  const [blockOffDate, setBlockOffDate] = useState(
    moment(startDate).startOf().format('MM-DD-YYYY'),
  );
  const { setMessage, setOpenSnackBar, setSeverity } = useSnackBar();

  const { id: clinicId } = clinic || {};
  const dateLabel = moment(date).format('MMMM DD, YYYY');
  const start = moment(startDate).startOf('day').toDate();
  const end = moment(endDate).startOf('day').toDate();
  const newDate = moment(date).startOf('day').toDate();
  const showLeftArrow = moment(newDate).isAfter(start, 'day');
  const showRightArrow = moment(newDate).isBefore(end, 'day');

  const sortedSlots = sortBy(slots, ['key']);
  const groupedSlots = groupBy(sortedSlots, ({ start_date: slotStartDate }) =>
    moment(slotStartDate, 'YYYY-MM-DD hh:mm:ss').format('MM-DD-YYYY'),
  );

  const buttonsDisabled = clinicStatus === 'Draft';
  const blockOffDisabled = buttonsDisabled && isEmpty(groupedSlots);

  const handleBack = () => {
    setSelectedDate(moment(selectedDate).subtract(1, 'days').toDate());
  };

  const handleNext = () => {
    setSelectedDate(moment(selectedDate).add(1, 'days').toDate());
  };

  const handleToday = () => {
    if (moment().isBefore(start, 'day') || moment().isAfter(end, 'day')) {
      setSelectedDate(start);
    } else {
      setSelectedDate(moment().toDate());
    }
  };

  const handleBlockOffChange = (e) => {
    setBlockOffDate(e.target.value);
  };

  const fetchClinic = useCallback(async () => {
    if (clinicId) {
      try {
        const { data } = await showClinic(clinicId, true);
        setSlots(data.data.times);
      } catch (error) {
        console.error(error);
      }
    }
  }, [slots]);

  const handleAppointmentModalOpen = () => {
    fetchClinic();
    setAppointmentModalOpen(true);
  };

  const blockOffMenuOpen = () => {
    fetchClinic();
    setBlockOffOpen(true);
  };

  const blockOffMenuClose = async () => {
    setBlockOffOpen(false);

    try {
      if (!isEmpty(slotsToBlock)) {
        await toggleBlockSlots(clinicId, slotsToBlock, 'block');
        setSlotsToBlock([]);
      }

      if (!isEmpty(slotsToUnblock)) {
        await toggleBlockSlots(clinicId, slotsToUnblock, 'unblock');
        setSlotsToUnblock([]);
      }
      await fetchClinic();
    } catch (error) {
      setMessage('Something went wrong. Failed to block/unblock slots');
      setSeverity('error');
      setOpenSnackBar(true);
    }
  };

  return (
    <Component>
      <DateBar>
        <Date>{dateLabel}</Date>

        {multiDays === 1 && (
          <Navigation>
            <IconButtonWrapper>
              {showLeftArrow && (
                <IconButton onClick={handleBack}>
                  <ChevronLeftIcon />
                </IconButton>
              )}
            </IconButtonWrapper>

            <IconButtonWrapper>
              {showRightArrow && (
                <IconButton onClick={handleNext}>
                  <ChevronRightIcon />
                </IconButton>
              )}
            </IconButtonWrapper>
          </Navigation>
        )}
      </DateBar>

      <ActionBar>
        <ActionButton
          variant="outlined"
          color="grey"
          onClick={handleToday}
          startIcon={<CalendarTodayIcon sx={{ fontSize: 16 }} />}
        >
          Start Date
        </ActionButton>

        <ActionButton
          variant="contained"
          color="primary"
          disabled={buttonsDisabled}
          onClick={handleAppointmentModalOpen}
        >
          Add Appointment
        </ActionButton>

        <ActionButton
          onClick={blockOffMenuOpen}
          variant="outlined"
          color="primary"
          disabled={buttonsDisabled || blockOffDisabled}
        >
          Add Block-Off
        </ActionButton>
      </ActionBar>

      <AddAppointmentModal
        appointmentModalOpen={appointmentModalOpen}
        setAppointmentModalOpen={setAppointmentModalOpen}
        clinic={clinic}
        slots={groupedSlots}
        fetchAppointments={fetchAppointments}
        fetchClinic={fetchClinic}
      />

      {!isEmpty(groupedSlots) && sortedSlots && blockOffOpen && (
        <ClickAwayListener onClickAway={blockOffMenuClose}>
          <BlockOffMenu>
            <Select
              options={Object.keys(groupedSlots).map((slot) => {
                if (isEmpty(groupedSlots)) {
                  return;
                }

                return {
                  label: moment(slot).format('ddd - MMM DD, YYYY'),
                  value: slot,
                };
              })}
              label="Available Dates"
              variant="outlined"
              onChange={handleBlockOffChange}
              value={blockOffDate}
              disableNone
              MenuProps={{ disablePortal: true }}
            />
            <BlockMenuHeading>Slots Blocked</BlockMenuHeading>
            <Slots>
              {groupedSlots[blockOffDate].map((time, index) => {
                const formatTime = moment(
                  time.start_date,
                  'YYYY-MM-DD hh:mm:ss',
                ).format('h:mma');

                const { key, blocked_count: blockedCount, count } = time;

                const blockedSlotsCount =
                  countBy(slotsToBlock, ({ key: prevKey }) => prevKey)[key] ||
                  0;
                const unblockedSlotsCount =
                  countBy(slotsToUnblock, ({ key: prevKey }) => prevKey)[key] ||
                  0;

                const trueBlockedCount =
                  blockedCount + blockedSlotsCount - unblockedSlotsCount;
                const trueCount =
                  count - blockedSlotsCount + unblockedSlotsCount;

                const handleBlockSlot = async () => {
                  const unblockedSlotIndex = findIndex(slotsToUnblock, [
                    'key',
                    key,
                  ]);

                  if (unblockedSlotIndex >= 0) {
                    setSlotsToUnblock((prevState) => {
                      const newState = [...prevState];
                      newState.splice(unblockedSlotIndex, 1);

                      return newState;
                    });
                  } else {
                    setSlotsToBlock((prevState) => {
                      if (!blockedSlotsCount || trueBlockedCount < count) {
                        return [...prevState, time];
                      }

                      return prevState;
                    });
                  }
                };

                const handleUnblockSlot = async () => {
                  const blockedSlotIndex = findIndex(slotsToBlock, [
                    'key',
                    key,
                  ]);

                  if (blockedSlotIndex >= 0) {
                    setSlotsToBlock((prevState) => {
                      const newState = [...prevState];
                      const slotIndex = findIndex(newState, ['key', key]);
                      newState.splice(slotIndex, 1);

                      return newState;
                    });
                  } else {
                    setSlotsToUnblock((prevState) => {
                      if (!unblockedSlotsCount || trueBlockedCount > 0) {
                        return [...prevState, time];
                      }

                      return prevState;
                    });
                  }
                };

                return (
                  <Slot>
                    <SlotButton
                      variant="outlined"
                      color="grey"
                      className={trueBlockedCount > 0 && 'active'}
                    >
                      {formatTime}
                    </SlotButton>

                    <BlockActions className={trueBlockedCount > 0 && 'active'}>
                      <IconButton
                        onClick={handleUnblockSlot}
                        disabled={trueCount === concurrentSlots}
                        color="primary"
                      >
                        <RemoveOutlinedIcon />
                      </IconButton>

                      <BlockCount>{trueBlockedCount}</BlockCount>

                      <IconButton
                        onClick={handleBlockSlot}
                        disabled={trueCount === 0}
                        color="primary"
                      >
                        <AddOutlinedIcon />
                      </IconButton>
                    </BlockActions>
                  </Slot>
                );
              })}
            </Slots>
          </BlockOffMenu>
        </ClickAwayListener>
      )}
    </Component>
  );
};

export default memo(ClinicToolbar);
